import * as React from "react";
import { Pressable, type PressableProps } from "react-native";
import Animated from "react-native-reanimated";
import { useCSSVariable } from "uniwind";
import { cn } from "@/shared/lib/utils";
import { usePressableScale } from "@/shared/hooks/use-pressable-scale";

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

type Size = "sm" | "md" | "lg";
type Shape = "square" | "circle";
type Surface = "transparent" | "tinted" | "filled";
type Tone = "neutral" | "brand" | "pink";

const sizeClasses: Record<Size, string> = {
  sm: "h-9 w-9",
  md: "h-10 w-10",
  lg: "h-12 w-12",
};

const shapeClasses: Record<Shape, string> = {
  square: "rounded-md",
  circle: "rounded-full",
};

type IconButtonProps = Omit<PressableProps, "children"> & {
  children: React.ReactNode;
  size?: Size;
  shape?: Shape;
  surface?: Surface;
  tone?: Tone;
  accessibilityLabel: string;
  className?: string;
};

export function IconButton({
  children,
  size = "md",
  shape = "circle",
  surface = "transparent",
  tone = "neutral",
  accessibilityLabel,
  className,
  disabled,
  onPressIn: callerPressIn,
  onPressOut: callerPressOut,
  ...props
}: IconButtonProps) {
  const { onPressIn, onPressOut, animatedStyle } = usePressableScale();
  const [surfaceColor] = useCSSVariable(["--color-surface"]) as [string];

  const tintedBg =
    surface === "tinted"
      ? tone === "neutral"
        ? { backgroundColor: surfaceColor }
        : undefined
      : undefined;

  const bgClass =
    surface === "transparent"
      ? "active:bg-accent/50"
      : surface === "tinted"
        ? tone === "brand"
          ? "bg-brand/15"
          : tone === "pink"
            ? "bg-pink/15"
            : ""
        : tone === "brand"
          ? "bg-brand"
          : tone === "pink"
            ? "bg-pink"
            : "bg-accent";

  return (
    <AnimatedPressable
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      disabled={disabled}
      onPressIn={(e) => {
        if (!disabled) onPressIn();
        callerPressIn?.(e);
      }}
      onPressOut={(e) => {
        if (!disabled) onPressOut();
        callerPressOut?.(e);
      }}
      className={cn(
        "items-center justify-center",
        sizeClasses[size],
        shapeClasses[shape],
        bgClass,
        disabled && "opacity-50",
        className
      )}
      style={[animatedStyle, tintedBg]}
      {...props}
    >
      {children}
    </AnimatedPressable>
  );
}
